import React, {useEffect, useMemo, useState} from "react";
import {observer} from "mobx-react";
import {useTranslation} from "react-i18next";
import {displayTransactionName} from "../../utils/transactionDisplay";
import { useHistory, useLocation } from "react-router-dom";
import {useStores} from "../../hooks/use-stores";
import {Box, CircularProgress, MenuItem, Select} from "@material-ui/core";
import PageTitle from "../../components/PageTitle";
import PrimarySwitch from "../../components/PrimarySwitch";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Button from "@material-ui/core/Button";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import moment from "moment";
import { createTheme, makeStyles, ThemeProvider } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import services from "../../services";
import {greenColor, redColor} from "../../components/styles";
import {useLocalObservable} from "mobx-react-lite";
import {extractErrorMessage, formatNumberOrZero} from "../../utils/helpers";
import range from "lodash/range";
import clsx from "clsx";
import ZelleDepositInstructionsPanel from "../../components/Wallet/ZelleDepositInstructionsPanel";

const formatterUSD = new Intl.NumberFormat('en-US', {
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
});

function getPendingRequestTypeKey(p) {
    if (p == null || typeof p !== 'object') return ''
    const v =
        p.RequestType ??
        p.requestType ??
        p.request_type ??
        p.Request_Type ??
        p.requestTypeName ??
        p.RequestTypeName ??
        p.depositRequestType ??
        p.DepositRequestType ??
        p.paymentRequestType ??
        p.PaymentRequestType
    const s = (v ?? '').toString().trim().toLowerCase()
    if (!s) return ''
    if (s.includes('zelle')) return 'zelle'
    if (s.includes('ach')) return 'ach'
    if (s.includes('etransfer') || s.includes('e-transfer') || s === 'email') return 'etransfer'
    return s
}

function isZellePendingRequest(p) {
    return getPendingRequestTypeKey(p) === 'zelle'
}

function getDepositTransactionId(p) {
    const id = p.depositTransactionID ?? p.DepositTransactionID ?? p.TransactionID ?? p.transactionID
    if (id == null || id === '') return undefined
    const n = Number(id)
    return Number.isNaN(n) ? undefined : n
}

function getPendingRowAmount(p) {
    const v = p.Amount ?? p.amount ?? p.total
    if (v == null || v === '') return 0
    const n = Number(v)
    return Number.isNaN(n) ? 0 : n
}

function getPendingRowDate(p) {
    return p.CreatedDate ?? p.createdDate ?? p.orderDate
}

function getTransactionGroupId(row) {
    const value = row?.groupID ?? row?.GroupID
    if (value == null || value === '') return null
    const n = Number(value)
    if (!Number.isNaN(n) && n === 0) return null
    return value
}

function isDepositRow(row) {
    return row?.isDeposit === true || row?.isDeposit === 1
}

// Matches usp_SC_GetTransactionHistory cashback split rows:
// "Perks", "Walmart Perks", "Royal Additional Perks", "Walmart Royal Additional Perks"
function isPerkSplitRow(row) {
    if (!isDepositRow(row)) return false
    return /(royal additional )?perks$/i.test((row?.transactionName || '').trim())
}

function buildTransactionGroups(transactions) {
    const list = Array.isArray(transactions) ? transactions : []
    const parentIndexByGroup = new Map()

    list.forEach((row, index) => {
        if (isPerkSplitRow(row)) return
        const groupID = getTransactionGroupId(row)
        if (groupID == null) return
        const key = String(groupID)
        if (!parentIndexByGroup.has(key)) {
            parentIndexByGroup.set(key, index)
        }
    })

    const childrenByParentIndex = new Map()
    const nestedPerkIndexes = new Set()
    list.forEach((row, index) => {
        if (!isPerkSplitRow(row)) return
        const groupID = getTransactionGroupId(row)
        if (groupID == null) return
        const parentIndex = parentIndexByGroup.get(String(groupID))
        if (parentIndex == null) return
        if (!childrenByParentIndex.has(parentIndex)) {
            childrenByParentIndex.set(parentIndex, [])
        }
        childrenByParentIndex.get(parentIndex).push(row)
        nestedPerkIndexes.add(index)
    })

    const groups = []
    list.forEach((row, index) => {
        if (nestedPerkIndexes.has(index)) return
        const groupID = getTransactionGroupId(row)
        const children = childrenByParentIndex.get(index) || []
        groups.push({
            key: groupID != null ? `${groupID}-${index}` : `row-${index}`,
            parent: row,
            children,
            expandable: children.length > 0,
        })
    })

    return groups
}

function getPendingTypeDisplay(p, t) {
    const rt = getPendingRequestTypeKey(p)
    const amount = getPendingRowAmount(p)
    if (rt === 'ach') return t('pending_type_ach_deposit')
    if (rt === 'zelle') return t('pending_type_zelle_deposit')
    if (rt === 'etransfer') return t('pending_type_etransfer_deposit')
    if (amount < 0) return t('pending_type_withdrawal')
    return t('pending_type_deposit')
}

const useStyles = makeStyles((theme) => ({
    amountCell: {
        gridTemplateColumns: "120px [col-start] 50px [col-start]"
    },
    greenLabel: {
        ...greenColor(theme),
        gridColumn: 1
    },
    total: {
        gridColumnStart: "col-start 1"

    },
    amountAdditionalValue: {
        gridColumn: 2
    },
    deposit: { ...greenColor(theme) },
    withdrawal: { ...redColor(theme) },
    pendingEWallet: {
        marginBottom: theme.spacing(5),
    },
    filter: {
        gap: theme.spacing(5),
    },
    inputLabel: {
        fontSize: '1.2rem'
    },
    green:{
        color:"#47a447"
    },
    table:{

        "& .MuiTableCell-root":{
            padding: 8
        }
    },
    zelleInstructionsDialogPaper: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        margin: theme.spacing(2),
        width: '100%',
        maxWidth: '100%',
        minHeight: '70vh',
        maxHeight: 'calc(100vh - 16px)',
        [theme.breakpoints.down('sm')]: {
            margin: theme.spacing(1),
            maxHeight: 'calc(100vh - 8px)',
        },
    },
    zelleInstructionsDialogTitle: {
        flexShrink: 0,
        margin: 0,
        paddingBottom: theme.spacing(1),
    },
    zelleInstructionsDialogTitleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        width: '100%',
        '& .MuiTypography-root': {
            flex: '1 1 auto',
            minWidth: 0,
            paddingRight: theme.spacing(1),
        },
    },
    zelleInstructionsCloseIconButton: {
        flexShrink: 0,
        padding: theme.spacing(1),
    },
    zelleInstructionsDialogContent: {
        flex: '1 1 0',
        minHeight: '70vh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        paddingTop: theme.spacing(2),
    },
    zelleInstructionsDialogActions: {
        flexShrink: 0,
        margin: 0,
        padding: theme.spacing(2),
        paddingBottom: `calc(${theme.spacing(2)}px + env(safe-area-inset-bottom, 0px))`,
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    pendingSectionHeader: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(1),
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: theme.spacing(2),
        },
    },
    pendingTitleGroup: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        '& > .MuiTypography-root': {
            marginBottom: `${theme.spacing(0.25)}px !important`,
        },
    },
    pendingDepositInstructionsLink: {
        fontSize: '0.875rem',
        fontWeight: 400,
        textAlign: 'left',
        [theme.breakpoints.up('sm')]: {
            flexShrink: 0,
        },
    },
    pendingCancelButton: {
        minWidth: 0,
        whiteSpace: 'nowrap',
    },
    expandCell: {
        width: 40,
        paddingLeft: 4,
        paddingRight: 0,
    },
    groupedRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    groupParentOpen: {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        '& td': {
            borderBottom: 'none',
            fontWeight: 600,
        },
        '& td:first-child': {
            borderLeft: '3px solid #007BFF',
        },
    },
    childRow: {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
        '& td': {
            color: theme.palette.text.secondary,
            borderBottom: 'none',
            fontSize: '0.875rem',
        },
        '& td:first-child': {
            borderLeft: '3px solid #007BFF',
        },
    },
    childRowLast: {
        '& td': {
            borderBottom: `3px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)'}`,
            paddingBottom: theme.spacing(1.5),
        },
    },
    childConnector: {
        width: 10,
        height: 12,
        marginLeft: theme.spacing(2.25),
        borderLeft: `2px solid ${theme.palette.text.disabled}`,
        borderBottom: `2px solid ${theme.palette.text.disabled}`,
    },
    childName: {
        paddingLeft: theme.spacing(2),
    },
    relatedHint: {
        display: 'block',
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 1.3,
    },
    detailsReceipts: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        textAlign: 'right',
    },

}));

function EWalletTransactions(){
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation("transactions");
    const { t: tw } = useTranslation("wallet");
    const { authStore, commonStore, statStore, transactionsStore } = useStores();
    const [selectedYear, setSelectedYear] = useState(2026);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [cancelDialogDepositId, setCancelDialogDepositId] = useState(null);
    const [cancelSubmitting, setCancelSubmitting] = useState(false);
    const [zelleInstructionsOpen, setZelleInstructionsOpen] = useState(false);
    const { currentUser } = authStore;
    const accountId8 = String(currentUser?.userID ?? '').slice(-8).padStart(8, '0');
    const zelleLightTheme = useMemo(() => createTheme({ palette: { type: 'light' } }), []);
    const { giftCardsStats } = statStore;

    useEffect(() => {
        void transactionsStore.loadEwalletTransactions({ year: selectedYear, month: selectedMonth });
        void transactionsStore.loadTransactions({ year: selectedYear, month: selectedMonth, transactionType: -2, showPurchases: true, showDeposits: true });
        void transactionsStore.loadPendingeWalletTransactions();
    }, [selectedYear, selectedMonth]);

    const {
        pendingeWallet,
        pendingeWalletTransactionsLoading,
        summary,
        transactions,
        eWalletTransactions,
        eWalletTransactionsLoading,
    } = transactionsStore;

    const hasZellePending = pendingeWallet.some((p) => isZellePendingRequest(p));

    const profileHomePath = (pathname = "") => {
        if (pathname.includes("/royalstarfish")) return "/royalstarfish/userprofile/profile";
        if (pathname.includes("/starfish")) return "/starfish/userprofile/profile";
        return "/userprofile/profile";
    };

    const handleTransactionDetails = () => {
        history.push(profileHomePath(location.pathname) + "/transactions");
    }

    const handleMonthChange = (e) => {
        setSelectedMonth(e.target.value);
    };

    const handleYearChange = (e) => {
        setSelectedYear(e.target.value);
    };

    const downloadCSV = async () => {
        try {
            const csvResponse = await services.UserProfile.Transactions().export(selectedYear, selectedMonth, -2, true, true);

            let hiddenDownloadLink = document.createElement('a');
            const blob = new Blob([csvResponse], { type: 'text/csv' });
            hiddenDownloadLink.href = window.URL.createObjectURL(blob);
            hiddenDownloadLink.download = 'transactions.csv';
            hiddenDownloadLink.click();
        } catch (e) {
            commonStore.warn("There was an error downloading your file. Please try again.");
        }
    };

    const closeCancelZelleDialog = () => {
        if (cancelSubmitting) return;
        setCancelDialogDepositId(null);
    };

    const confirmCancelZelleDeposit = async () => {
        if (cancelDialogDepositId == null) return;
        setCancelSubmitting(true);
        try {
            await services.UserProfile.Wallet().cancelZelleDepositRequest(cancelDialogDepositId);
            commonStore.success(t('zelle_cancel_pending_success'));
            setCancelDialogDepositId(null);
            await Promise.all([
                transactionsStore.loadEwalletTransactions({ year: selectedYear, month: selectedMonth }),
                transactionsStore.loadPendingeWalletTransactions(),
            ]);
        } catch (e) {
            commonStore.warn(extractErrorMessage(e) || t('zelle_cancel_pending_error'));
        } finally {
            setCancelSubmitting(false);
        }
    };

    const localStore = useLocalObservable(() => ({
        collapsedGroupIds: {},
        isGroupOpen(groupId) {
            return !this.collapsedGroupIds[groupId];
        },
        toggleGroup(groupId) {
            if (this.collapsedGroupIds[groupId]) {
                const next = { ...this.collapsedGroupIds };
                delete next[groupId];
                this.collapsedGroupIds = next;
            } else {
                this.collapsedGroupIds = { ...this.collapsedGroupIds, [groupId]: true };
            }
        },
        resetCollapsed() {
            this.collapsedGroupIds = {};
        },
        get totalCashback(){
            return eWalletTransactions.filter(t => t.transactionName === "1.5% Wallet Cashback").reduce((acc, t) => {
                return acc + t.total;
            }, 0);
        }
    }));

    const groupedTransactions = buildTransactionGroups(transactions);

    useEffect(() => {
        localStore.resetCollapsed();
    }, [selectedYear, selectedMonth]);

    return <>
        {(eWalletTransactionsLoading || eWalletTransactionsLoading == null)
            ? <CircularProgress />
            : <>
                {pendingeWalletTransactionsLoading && <CircularProgress />}
                {!pendingeWalletTransactionsLoading && pendingeWallet.length > 0 && (
                    <>
                        <Box className={classes.pendingSectionHeader}>
                            <Box className={classes.pendingTitleGroup}>
                                <PageTitle>{t('pendingeWalletTransactions')}</PageTitle>
                                {hasZellePending && (
                                    <Link
                                        component="button"
                                        type="button"
                                        color="primary"
                                        underline="always"
                                        className={classes.pendingDepositInstructionsLink}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setZelleInstructionsOpen(true);
                                        }}
                                    >
                                        {tw('add_money.zelle_deposit_instructions')}
                                    </Link>
                                )}
                            </Box>
                        </Box>
                   
                   
                   
                        <TableContainer className={classes.pendingEWallet}>
                            <Table className={classes.table}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('date')}</TableCell>
                                        <TableCell>{t('type')}</TableCell>
                                        <TableCell align="right">{t('amount')}</TableCell>
                                        <TableCell align="right">{t('actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pendingeWallet.map((p) => {
                                        const amount = getPendingRowAmount(p);
                                        const depositTxId = getDepositTransactionId(p);
                                        const showCancel = isZellePendingRequest(p) && depositTxId != null;
                                        const dateRaw = getPendingRowDate(p);
                                        return (
                                            <TableRow key={p.TransactionID ?? p.transactionID}>
                                                <TableCell>
                                                    {dateRaw ? moment.utc(dateRaw).format('MM/DD') : ''}
                                                </TableCell>
                                                <TableCell>
                                                    {getPendingTypeDisplay(p, t)}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    className={clsx({
                                                        [classes.deposit]: amount > 0,
                                                        [classes.withdrawal]: amount < 0,
                                                    })}
                                                >
                                                    {amount > 0 ? '+$' : '-$'}
                                                    {amount}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {showCancel ? (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                            className={classes.pendingCancelButton}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setCancelDialogDepositId(depositTxId);
                                                            }}
                                                        >
                                                            {t('zelle_cancel_deposit')}
                                                        </Button>
                                                    ) : null}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
                <PageTitle>{t("ewalletTransactionHistory")}</PageTitle>
                <Box className={classes.detailsReceipts}>
                    <Typography variant="body2">
                        {t("toDisplayDetailsReceipts")}
                    </Typography>
                    <Button onClick={e=>handleTransactionDetails()} size="small"
                                variant={"contained"}
                                color="primary">
                        {t("transactionDetails")}
                    </Button>
                </Box>
                
                <Box display="flex" justifyContent="flex-end" className={classes.filter}>
                    <FormControl margin={"normal"} >
                        <InputLabel className={classes.inputLabel}>{t("month")}</InputLabel>
                        <Select value={selectedMonth} onChange={handleMonthChange} label={t("month")} style={{textTransform:"capitalize"}} >
                            <MenuItem value={-1}>{t("all")}</MenuItem>
                            {range(1, 13).map(m => <MenuItem key={m} value={m} style={{textTransform:"capitalize"}}>
                                {new Date( 2000, m-1).toLocaleString(commonStore.language, { month: "long" })}
                            </MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl margin={"normal"} >
                        <InputLabel className={classes.inputLabel}>{t("year")}</InputLabel>
                        <Select value={selectedYear} onChange={handleYearChange} label={t("year")}>
                            <MenuItem value={2026}>2026</MenuItem>
                            <MenuItem value={2025}>2025</MenuItem>
                            <MenuItem value={2024}>2024</MenuItem>
                            <MenuItem value={2023}>2023</MenuItem>
                            <MenuItem value={2022}>2022</MenuItem>
                            <MenuItem value={2021}>2021</MenuItem>
                            <MenuItem value={2020}>2020</MenuItem>
                        </Select>
                    </FormControl>
                    {!commonStore.isNativeApp && <Button onClick={downloadCSV} startIcon={<CloudDownloadIcon />}>{t("export")}</Button>}
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.expandCell} />
                                <TableCell>{t("date")}</TableCell>
                                <TableCell>{t("name")}</TableCell>
                                <TableCell align="right">{t("cashEarned")}</TableCell>
                                <TableCell align="right">{t("amount")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groupedTransactions.map((group) => {
                                const { parent, children, expandable, key } = group;
                                if (!parent) return null;
                                const isOpen = localStore.isGroupOpen(key);
                                const parentKey = parent.transactionID ?? parent.orderID ?? key;
                                return (
                                    <React.Fragment key={key}>
                                        <TableRow
                                            className={clsx({
                                                [classes.groupedRow]: expandable,
                                                [classes.groupParentOpen]: expandable && isOpen,
                                            })}
                                            onClick={expandable ? () => localStore.toggleGroup(key) : undefined}
                                        >
                                            <TableCell className={classes.expandCell}>
                                                {expandable && (
                                                    <IconButton
                                                        aria-label={isOpen ? t("collapseRelated") : t("expandRelated")}
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            localStore.toggleGroup(key);
                                                        }}
                                                    >
                                                        {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {moment(parent.orderDate).format("LL")}
                                            </TableCell>
                                            <TableCell>
                                                {displayTransactionName(parent.transactionName)}
                                                {expandable && !isOpen && (
                                                    <Typography component="span" className={classes.relatedHint}>
                                                        {t("relatedCount", { count: children.length })}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">{formatterUSD.format(parent.cashEarned)}</TableCell>
                                            <TableCell align="right">{formatterUSD.format(parent.total)}</TableCell>
                                        </TableRow>
                                        {expandable && isOpen && children.map((child, childIndex) => {
                                            const isLastChild = childIndex === children.length - 1;
                                            return (
                                            <TableRow
                                                key={child.transactionID ?? `${parentKey}-related-${childIndex}`}
                                                className={clsx(classes.childRow, { [classes.childRowLast]: isLastChild })}
                                            >
                                                <TableCell className={classes.expandCell}>
                                                    <Box className={classes.childConnector} />
                                                </TableCell>
                                                <TableCell>
                                                    {moment(child.orderDate).format("LL")}
                                                </TableCell>
                                                <TableCell className={classes.childName}>
                                                    {displayTransactionName(child.transactionName)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatterUSD.format(child.cashEarned)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatterUSD.format(child.total)}
                                                </TableCell>
                                            </TableRow>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box paddingTop={4}>
                    {t("numberOfGiftCards")} { summary.TotalOrders }
                </Box>
                <Box paddingTop={2}>
                    {t("totalSpent")} ${formatNumberOrZero(summary.TotalSpend, 2 )}
                </Box>
                <Box paddingTop={2}>
                    {t("totalPerks")} ${formatNumberOrZero( summary.TotalPerks, 2 )}
                </Box>
                <Box paddingTop={2} paddingBottom={5}>
                    {t("totalCashback")} ${formatNumberOrZero( localStore.totalCashback, 2 )}
                </Box>
            </>}

        <Dialog
            open={zelleInstructionsOpen}
            onClose={() => setZelleInstructionsOpen(false)}
            fullWidth
            maxWidth="lg"
            PaperProps={{ className: classes.zelleInstructionsDialogPaper }}
        >
            <DialogTitle disableTypography className={classes.zelleInstructionsDialogTitle}>
                <Box className={classes.zelleInstructionsDialogTitleRow}>
                    <Typography variant="h6" component="span">
                        {tw('add_money.zelle_complete_title')}
                    </Typography>
                    <IconButton
                        className={classes.zelleInstructionsCloseIconButton}
                        edge="end"
                        aria-label={tw('add_money.zelle_instructions_close')}
                        onClick={() => setZelleInstructionsOpen(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers className={classes.zelleInstructionsDialogContent}>
                <ThemeProvider theme={zelleLightTheme}>
                    <ZelleDepositInstructionsPanel
                        noPaper
                        hideCloseButton
                        t={tw}
                        accountId8={accountId8}
                        onClose={() => setZelleInstructionsOpen(false)}
                        billingError={null}
                    />
                </ThemeProvider>
            </DialogContent>
            <DialogActions className={classes.zelleInstructionsDialogActions}>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => setZelleInstructionsOpen(false)}
                >
                    {tw('add_money.zelle_instructions_close')}
                </Button>
            </DialogActions>
        </Dialog>

        <Dialog open={cancelDialogDepositId != null} onClose={closeCancelZelleDialog}>
            <DialogTitle>{t('zelle_cancel_pending_title')}</DialogTitle>
            <DialogContent>
                <DialogContentText>{t('zelle_cancel_pending_body')}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={closeCancelZelleDialog} color="primary" disabled={cancelSubmitting}>
                    {t('zelle_cancel_pending_dismiss')}
                </Button>
                <Button onClick={confirmCancelZelleDeposit} color="primary" variant="contained" disabled={cancelSubmitting}>
                    {cancelSubmitting ? '...' : t('zelle_cancel_pending_confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    </>
}


export default observer(EWalletTransactions);